import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Ribbon } from "@/components/Layout";
import { RdvDialog } from "@/components/RdvDialog";
import { RdvApercu } from "@/components/RdvApercu";
import { useCreerRdv } from "@/components/CreerRdv";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";
import { RefreshCw, Plus, ChevronLeft, ChevronRight, Trash2, Pencil, Check, X, Move, ArrowLeft, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DRAG_COLORS } from "@/lib/agendaDragColors";
import { toneForTypeId, toneForType, CARE_TYPE_TONES } from "@/lib/careTones";
import { SWIPE, SWIPE_COLORS, weekRangeLabel } from "@/lib/agendaSwipe";
import { useWeekSwipe } from "@/hooks/useWeekSwipe";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const DAYS_SHORT = ["L", "M", "M", "J", "V", "S", "D"];
const MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const HOURS = Array.from({ length: 12 }, (_, i) => i + 8);

const sow = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
const iso = (d) => new Date(d).toISOString();
const toLocalDT = (isoStr) => { if (!isoStr) return ""; const d = new Date(isoStr); const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };
const fmtRange = (a, b) => { const s = new Date(a), e = new Date(b); const opt = { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }; return `${s.toLocaleString("fr-FR", opt)} → ${e.toLocaleString("fr-FR", opt)}`; };
const sameDay = (a, b) => a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
const isToday = (d) => sameDay(d, new Date());
// Durée lisible : minutes sous 1 h, sinon « 1h » / « 1h10 ».
const fmtDuree = (m) => { if (m == null) return ""; if (m < 60) return `${m} min`; const h = Math.floor(m / 60), r = m % 60; return r ? `${h}h${String(r).padStart(2, "0")}` : `${h}h`; };
// Statuts « terminés » : la carte n'est pas saisissable (pas de déplacement).
const LOCKED_STATUTS = new Set(["Soin délivré", "Clôturé", "Annulé"]);

const TYPE_STYLE = {
  soin: "bg-emerald-50 border-emerald-300 text-emerald-900",
  absence: "bg-slate-100 border-slate-300 text-slate-700",
  hors_soin: "bg-stone-50 border-stone-200 text-stone-700",
};
// eslint-disable-next-line no-unused-vars
const _TYPE_STYLE_LEGACY = TYPE_STYLE;
// Couleur de la pastille de statut (indépendante de la couleur du type de soin).
const STATUT_DOT = (statut) => (statut === "Soin délivré" || statut === "Clôturé") ? "#16a34a" : statut === "Annulé" ? "#c86f4b" : "#5f8f73";

function rangeFor(view, anchor) {
  if (view === "semaine") { const s = sow(anchor); const e = new Date(s); e.setDate(e.getDate() + 7); return { start: s, end: e }; }
  if (view === "mois") { const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1); const s = sow(first); const e = new Date(s); e.setDate(e.getDate() + 42); return { start: s, end: e }; }
  const qStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const e = new Date(anchor.getFullYear(), anchor.getMonth() + 3, 1);
  return { start: sow(qStart), end: e };
}

export default function Agenda() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const { openCreerRdv } = useCreerRdv();
  const [view, setView] = useState("semaine");
  const [anchor, setAnchor] = useState(new Date());
  const [rdvs, setRdvs] = useState([]);
  const [tab, setTab] = useState("agenda");
  const [patients, setPatients] = useState([]);
  const [careTypes, setCareTypes] = useState([]);
  const [secteurs, setSecteurs] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [prefillPatient, setPrefillPatient] = useState("");
  const [slotDlg, setSlotDlg] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState(null);
  const [hours, setHours] = useState([]);
  const [defaultType, setDefaultType] = useState("");
  const [pas, setPas] = useState(30);
  const [weekFlash, setWeekFlash] = useState(false);   // surbrillance des dates après navigation
  const skipLoadRef = useRef(false);                    // évite un double chargement après navigateWeek
  const swipeGoRef = useRef(null);                      // méthode go(dir) exposée par le hook de geste

  const load = async () => {
    if (skipLoadRef.current) { skipLoadRef.current = false; return; }
    const { start, end } = rangeFor(view, anchor);
    const { data } = await api.get("/rdvs", { params: { start: iso(start), end: iso(end) } });
    setRdvs(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [view, anchor]);
  useEffect(() => {
    const onCreated = (e) => { const d = e?.detail?.date; if (d) setAnchor(new Date(d)); else load(); };
    const onChanged = () => load();
    window.addEventListener("rdv-created", onCreated);
    window.addEventListener("rdv-changed", onChanged);
    return () => { window.removeEventListener("rdv-created", onCreated); window.removeEventListener("rdv-changed", onChanged); };
    /* eslint-disable-next-line */
  }, [view, anchor]);
  useEffect(() => {
    api.get("/patients").then((r) => setPatients(r.data)).catch(() => {});
    api.get("/settings/care-types").then((r) => setCareTypes(r.data)).catch(() => {});
    api.get("/settings").then((r) => { setDefaultType(r.data?.care_type_defaut || ""); setPas(r.data?.pas_creneaux || 30); }).catch(() => {});
    api.get("/settings/secteurs").then((r) => setSecteurs(r.data)).catch(() => {});
    api.get("/calendar/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/opening-hours").then((r) => setHours(r.data)).catch(() => {});
    if (params.get("new")) { setPrefillPatient(params.get("patient") || ""); setDlg(true); setParams({}); }
    /* eslint-disable-next-line */
  }, []);

  const sync = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/calendar/sync", null, { params: { periode: "mois" } }); toast.success(`${data.imported} soin(s) importé(s)`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  const shift = (dir) => {
    const d = new Date(anchor);
    if (view === "semaine") d.setDate(d.getDate() + dir * 7);
    else if (view === "mois") d.setMonth(d.getMonth() + dir);
    else d.setMonth(d.getMonth() + dir * 3);
    setAnchor(d);
  };

  const openDay = (day) => { setAnchor(day); setView("semaine"); };
  const rdvsOn = (day) => rdvs.filter((r) => sameDay(new Date(r.date), day));

  const rdvId = params.get("rdv");
  const orderedIds = rdvs.filter((r) => r.type_rdv !== "absence").map((r) => r.id);
  const openRdv = (id) => { const sp = new URLSearchParams(params); sp.set("rdv", id); setParams(sp); };
  const closeRdv = () => { const sp = new URLSearchParams(params); sp.delete("rdv"); setParams(sp); };

  // Glisser-déplacer / étirer un RDV : MAJ optimiste + PATCH (met à jour le RDV
  // et la fiche de soin liée côté backend). Renvoie true si enregistré (le
  // bandeau d'annulation est géré par la grille), false sinon (retour arrière).
  const patchRdv = async (id, body) => {
    const prev = rdvs.find((r) => r.id === id);
    setRdvs((p) => p.map((r) => (r.id === id ? { ...r, ...body } : r)));
    try { await api.patch(`/rdvs/${id}`, body); load(); return true; }
    catch (e) { setRdvs((p) => p.map((r) => (r.id === id ? prev : r))); toast.error("Modification non enregistrée, réessayez"); return false; }
  };

  // Navigation de semaine animée (geste + flèches) : charge la semaine visée,
  // ferme l'aperçu RDV ouvert et déclenche la surbrillance des dates. Lève en
  // cas d'échec pour que le hook rejoue la semaine précédente.
  const navigateWeek = async (dir) => {
    const d = new Date(anchor); d.setDate(d.getDate() + dir * 7);
    const { start, end } = rangeFor("semaine", d);
    try {
      const { data } = await api.get("/rdvs", { params: { start: iso(start), end: iso(end) } });
      skipLoadRef.current = true; // l'effet sur anchor ne rechargera pas
      setRdvs(data);
      setAnchor(d);
      if (rdvId) closeRdv();
      setWeekFlash(true);
      setTimeout(() => setWeekFlash(false), SWIPE.FLASH_MS);
    } catch (e) {
      toast.error("Semaine non chargée, réessayez");
      throw e;
    }
  };

  const label = view === "semaine"
    ? (() => { const s = sow(anchor); const e = new Date(s); e.setDate(e.getDate() + 6); return `${s.getDate()} ${MONTHS[s.getMonth()]} – ${e.getDate()} ${MONTHS[e.getMonth()]}`; })()
    : view === "mois" ? `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`
    : `${MONTHS[anchor.getMonth()]} – ${MONTHS[(anchor.getMonth() + 2) % 12]} ${anchor.getFullYear()}`;

  return (
    <div>
      <div className="sx-seg mb-4">
        {[["agenda", "Vue Agenda"], ["creneaux", "Créneaux d'ouverture & fermeture"]].map(([k, l]) => (
          <button key={k} data-testid={`agenda-tab-${k}`} onClick={() => setTab(k)} className="sx-seg-item" data-active={tab === k}>{l}</button>
        ))}
      </div>

      {tab === "agenda" && (
        <>
          <Ribbon groups={[
            { name: "Navigation", children: (
              <div className="flex items-center gap-1">
                <button data-testid="week-prev" onClick={() => (view === "semaine" && swipeGoRef.current) ? swipeGoRef.current(-1) : shift(-1)} className="p-2 rounded-lg hover:bg-slate-100"><ChevronLeft className="w-4 h-4" /></button>
                <span data-testid="week-label" className="text-sm font-medium px-2 min-w-[150px] text-center" style={{ color: weekFlash ? SWIPE_COLORS.rangeFlashInk : "#141821", background: weekFlash ? SWIPE_COLORS.rangeFlashBg : "transparent", padding: "3px 6px", borderRadius: 7, transition: "background .5s ease, color .5s ease" }}>{label}</span>
                <button data-testid="week-next" onClick={() => (view === "semaine" && swipeGoRef.current) ? swipeGoRef.current(1) : shift(1)} className="p-2 rounded-lg hover:bg-slate-100"><ChevronRight className="w-4 h-4" /></button>
                <button data-testid="week-today" onClick={() => setAnchor(new Date())} className="ml-1 px-2.5 py-1.5 text-xs rounded-lg border border-slate-200 hover:bg-slate-50">Aujourd'hui</button>
              </div>) },
            { name: "Vue", children: (
              <div className="sx-seg">
                {[["semaine", "Semaine"], ["mois", "Mois"], ["trimestre", "Trimestre"]].map(([k, l]) => (
                  <button key={k} data-testid={`view-${k}`} onClick={() => setView(k)} className="sx-seg-item" data-active={view === k}>{l}</button>
                ))}
              </div>) },
            { name: "Synchro Google", children: (
              <button data-testid="sync-google-btn" onClick={sync} disabled={syncing} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50 disabled:opacity-50">
                <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />Synchroniser</button>) },
          ]} />

          {status && !status.configured && (
            <div className="mb-4 text-sm bg-yellow-50 border border-yellow-200 text-yellow-900 rounded-lg px-4 py-2.5">
              Google Agenda non connecté — configurez la connexion dans <span className="font-semibold cursor-pointer underline" onClick={() => nav("/parametres")}>Paramètres &gt; Connexions</span>.
            </div>
          )}

          {view === "semaine" && <><AgendaLegend rdvs={rdvs} careTypes={careTypes} /><WeekGrid anchor={anchor} rdvs={rdvs} nav={nav} hours={hours} pas={pas} selectedId={rdvId} careTypes={careTypes} onOpenRdv={openRdv} onDropRdv={patchRdv} onNavigate={navigateWeek} swipeGoRef={swipeGoRef} onSlot={(iso) => { if (rdvId) closeRdv(); else openCreerRdv({ date: iso }); }} /></>}
          {view === "mois" && <MonthGrid anchor={anchor} rdvsOn={rdvsOn} careTypes={careTypes} openDay={openDay} onOpenRdv={openRdv} />}
          {view === "trimestre" && <QuarterGrid anchor={anchor} rdvsOn={rdvsOn} careTypes={careTypes} openDay={openDay} />}
        </>
      )}

      {tab === "creneaux" && (
        <div className="space-y-4">
          <OpeningHours hours={hours} onSaved={(h) => setHours(h)} />
          <FermeturesPonctuelles onChanged={load} />
        </div>
      )}

      <RdvDialog open={dlg} onClose={() => setDlg(false)} patients={patients} careTypes={careTypes} secteurs={secteurs} prefillPatient={prefillPatient} defaultType={defaultType} onSaved={() => { setDlg(false); load(); }} />
      <SlotDialog open={slotDlg} onClose={() => setSlotDlg(false)} onSaved={() => { setSlotDlg(false); load(); }} />
      {rdvId && <RdvApercu rdvId={rdvId} orderedIds={orderedIds} onClose={closeRdv} onNavigate={openRdv} onSaved={load} careTypes={careTypes} secteurs={secteurs} patients={patients} pas={pas} />}
    </div>
  );
}

// Ligne rouge de l'heure actuelle (prompt 15) : colonne d'aujourd'hui uniquement,
// mise à jour toutes les 60 s, masquée hors plage horaire, passe sous les cartes.
function NowLine({ START, END, PX }) {
  const [now, setNow] = useState(new Date());
  const ref = useRef(null);
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(id); }, []);
  useEffect(() => { ref.current?.scrollIntoView({ block: "center", behavior: "auto" }); }, []);
  const h = now.getHours() + now.getMinutes() / 60;
  if (h < START || h >= END) return null;
  return (
    <div ref={ref} data-testid="now-line" style={{ position: "absolute", top: (h - START) * PX, left: 0, right: 0, height: 2, background: DRAG_COLORS.nowLine, pointerEvents: "none", zIndex: 2 }}>
      <span style={{ position: "absolute", left: -4, top: -3, width: 8, height: 8, borderRadius: 99, background: DRAG_COLORS.nowLine }} />
    </div>
  );
}

// Légende des types de soin présents dans la semaine (alignée à droite), masquée s'il n'y en a qu'un.
function AgendaLegend({ rdvs, careTypes }) {
  const seen = new Set(); const present = [];
  (rdvs || []).forEach((r) => { if (r.type_rdv === "absence") return; const key = r.type_soin || "__none"; if (seen.has(key)) return; seen.add(key); present.push(careTypes.find((c) => c.id === r.type_soin) || null); });
  if (present.length <= 1) return null;
  return (
    <div className="flex items-center justify-end flex-wrap gap-x-2.5 gap-y-1 mb-2" data-testid="agenda-legend">
      {present.map((t, i) => { const tone = t ? toneForType(t) : CARE_TYPE_TONES[0]; return (
        <span key={i} className="inline-flex items-center gap-1.5 text-[12px]" style={{ color: "#3a3f4a" }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: tone.bg, border: `1px solid ${tone.border}` }} />{t ? t.nom : "Sans type"}
        </span>); })}
    </div>
  );
}

function WeekGrid({ anchor, rdvs, nav, hours, pas = 30, selectedId, careTypes = [], onOpenRdv, onSlot, onDropRdv, onNavigate, swipeGoRef }) {
  const START = 0, END = 24, PX = 56;
  const colRefs = useRef([]);
  const dragRef = useRef(null);       // info du geste actif
  const pendingRef = useRef(null);    // appui en attente d'activation (clic prolongé / seuil 4px)
  const suppressClickRef = useRef(false);
  const undoTimerRef = useRef(null);
  const [drag, setDrag] = useState(null);
  const [pulseId, setPulseId] = useState(null); // pulsation verte après dépôt
  const [undo, setUndo] = useState(null);        // bandeau d'annulation
  const s = sow(anchor);
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(s); d.setDate(d.getDate() + i); return d; });
  const dayHeight = (END - START) * PX;
  const topFor = (dt) => (dt.getHours() + dt.getMinutes() / 60 - START) * PX;
  const toMin = (t) => { const [a, b] = (t || "0:0").split(":").map(Number); return a * 60 + b; };
  const ohFor = (dow) => (hours || []).find((h) => h.jour === dow);
  const HATCH = "repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(20,24,33,0.09) 8px, rgba(20,24,33,0.09) 17px)";
  const DAYS_ABBR = ["Lun.", "Mar.", "Mer.", "Jeu.", "Ven.", "Sam.", "Dim."];
  const MONTHS_ABBR = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
  const cols = `56px repeat(7, 1fr)`;
  const DC = DRAG_COLORS;
  const isLocked = (r) => LOCKED_STATUTS.has(r.statut);

  const cardRef = useRef(null);
  const [gridH, setGridH] = useState(600);
  // Hauteur : l'agenda occupe la hauteur dispo jusqu'en bas, moins la marge droite.
  useEffect(() => {
    const compute = () => { const el = cardRef.current; if (!el) return; const r = el.getBoundingClientRect(); const rightMargin = Math.max(window.innerWidth - r.right, 8); setGridH(Math.max(window.innerHeight - r.top - rightMargin, 320)); };
    compute(); window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);
  // Geste « changer de semaine » (Prompt 16) : seule la grille bouge, le reste
  // de la carte reste fixe ; désactivé pendant un glisser/étirer de RDV.
  const swipe = useWeekSwipe({ cardRef, onNavigate, disabled: !!drag, computeRange: (d) => weekRangeLabel(anchor, d) });
  useEffect(() => { if (swipeGoRef) swipeGoRef.current = swipe.go; }, [swipeGoRef, swipe.go]);
  // Rendre visible le RDV sélectionné s'il passe sous le volet flottant.
  useEffect(() => {
    if (!selectedId) return;
    const el = document.querySelector(`[data-testid='rdv-card-${selectedId}']`);
    el?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [selectedId]);

  const hm = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(Math.round(m % 60)).padStart(2, "0")}`;
  const snap = (m) => Math.round(m / pas) * pas;
  const pointerToDayMin = (e) => {
    for (let i = 0; i < 7; i++) {
      const el = colRefs.current[i]; if (!el) continue;
      const r = el.getBoundingClientRect();
      if (e.clientX >= r.left && e.clientX < r.right) return { dayIdx: i, min: START * 60 + ((e.clientY - r.top) / PX) * 60 };
    }
    return null;
  };

  // Conflit sur le créneau visé : chevauchement = bloquant ; jour fermé = autorisé
  // mais signalé en rouge (déplacement possible sur une zone fermée, prompt corrigé).
  const evalTarget = (dayIdx, sMin, d, excludeId) => {
    const day = days[dayIdx];
    const oh = ohFor((day.getDay() + 6) % 7);
    const closed = !oh || !oh.ouvert;
    const nEnd = sMin + d;
    for (const x of rdvs) {
      if (x.id === excludeId) continue;
      if (!sameDay(new Date(x.date), day)) continue;
      const xs = new Date(x.date); const xm = xs.getHours() * 60 + xs.getMinutes(); const xe = xm + (x.duree_min || 60);
      if (sMin < xe && xm < nEnd) return { name: x.patient_nom || x.titre || "RDV", closed, blocked: true };
    }
    return { name: null, closed, blocked: false };
  };

  // Active l'état « saisi » à partir de l'appui en attente.
  const activate = (mode) => {
    const pc = pendingRef.current; if (!pc) return;
    if (pc.timer) clearTimeout(pc.timer);
    pc.activated = true;
    const info = { id: pc.r.id, r: pc.r, mode, startMin: pc.startMin, dur: pc.dur, dayIdx: pc.di, grabOffset: pc.grabOffset, moved: false,
      ghost: { dayIdx: pc.di, startMin: pc.startMin, dur: pc.dur }, name: null, closed: false, blocked: false };
    dragRef.current = info; setDrag(info);
    document.body.style.userSelect = "none";
  };

  const trackMove = (ev) => {
    const cur = dragRef.current; if (!cur) return;
    const pt = pointerToDayMin(ev); if (!pt) return;
    cur.moved = true;
    let dayIdx = cur.dayIdx, sMin = cur.startMin, d = cur.dur;
    if (cur.mode === "move") {
      dayIdx = pt.dayIdx;
      sMin = Math.max(START * 60, Math.min(snap(pt.min - cur.grabOffset), END * 60 - d));
    } else {
      d = Math.max(pas, snap(pt.min - cur.startMin));
      if (cur.startMin + d > END * 60) d = END * 60 - cur.startMin;
    }
    const t = evalTarget(dayIdx, sMin, d, cur.id);
    const ghost = { dayIdx, startMin: sMin, dur: d };
    dragRef.current = { ...cur, ghost, name: t.name, closed: t.closed, blocked: t.blocked };
    setDrag((prev) => (prev ? { ...prev, ghost, name: t.name, closed: t.closed, blocked: t.blocked } : prev));
  };

  const triggerPulse = (id) => { setPulseId(id); setTimeout(() => setPulseId((p) => (p === id ? null : p)), 1400); };

  const showUndo = (id, prev, mode, g) => {
    const name = dragRef.current?.r?.patient_nom || dragRef.current?.r?.titre || "RDV";
    const day = days[g.dayIdx];
    const label = mode === "resize"
      ? `RDV redimensionné · ${name} · Durée : ${g.dur} min`
      : `RDV déplacé · ${name} · ${DAYS_ABBR[g.dayIdx]} ${day.getDate()} ${MONTHS_ABBR[day.getMonth()]} · ${hm(g.startMin)}`;
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndo({ id, prev, label });
    undoTimerRef.current = setTimeout(() => setUndo(null), 6000);
  };

  const finishDrag = async () => {
    const cur = dragRef.current; dragRef.current = null;
    document.body.style.userSelect = "";
    setDrag(null);
    if (!cur || !cur.moved) return;
    suppressClickRef.current = true;
    const g = cur.ghost;
    if (g.dayIdx === cur.dayIdx && g.startMin === cur.startMin && g.dur === cur.dur) return; // rien n'a changé
    if (cur.blocked) { toast.error("Créneau occupé : RDV non déplacé"); return; }
    const targetDay = days[g.dayIdx];
    const dt = new Date(targetDay); dt.setHours(0, g.startMin, 0, 0);
    const prev = { date: new Date(cur.r.date).toISOString(), duree_min: cur.dur };
    const body = { date: dt.toISOString(), duree_min: g.dur };
    const ok = await onDropRdv(cur.id, body);
    if (!ok) return;
    triggerPulse(cur.id);
    onOpenRdv && onOpenRdv(cur.id);
    showUndo(cur.id, prev, cur.mode, g);
  };

  const onPendingMove = (e) => {
    const pc = pendingRef.current; if (!pc) return;
    if (!pc.activated && (Math.abs(e.clientX - pc.x) > 4 || Math.abs(e.clientY - pc.y) > 4)) activate(pc.mode);
    if (pendingRef.current?.activated) trackMove(e);
  };
  const onPendingUp = () => {
    window.removeEventListener("pointermove", onPendingMove);
    window.removeEventListener("pointerup", onPendingUp);
    const pc = pendingRef.current; pendingRef.current = null;
    if (pc?.timer) clearTimeout(pc.timer);
    if (pc?.activated) finishDrag();
  };
  const beginPending = (e, r, di, mode) => {
    if (!onDropRdv || isLocked(r)) return;
    if (mode === "resize") e.stopPropagation();
    const st0 = new Date(r.date);
    const startMin = st0.getHours() * 60 + st0.getMinutes();
    const dur = r.duree_min || 60;
    const p = pointerToDayMin(e);
    const grabOffset = mode === "move" && p ? p.min - startMin : 0;
    pendingRef.current = { x: e.clientX, y: e.clientY, r, di, startMin, dur, grabOffset, mode, activated: false,
      timer: mode === "move" ? setTimeout(() => { if (pendingRef.current && !pendingRef.current.activated) activate("move"); }, 200) : null };
    window.addEventListener("pointermove", onPendingMove);
    window.addEventListener("pointerup", onPendingUp);
    if (mode === "resize") activate("resize");
  };

  const doUndo = async () => {
    if (!undo) return;
    const { id, prev } = undo;
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndo(null);
    const ok = await onDropRdv(id, prev);
    if (ok) triggerPulse(id);
  };

  return (
    <>
    <div ref={cardRef} className="sx-card" style={{ height: gridH, overflow: "hidden", position: "relative", overscrollBehaviorX: "contain" }}>
      <div style={{ height: "100%", overflowY: "auto", overflowX: "hidden", touchAction: "pan-y" }}>
      <div style={swipe.trackStyle}>
      <div className="min-w-[900px]">
        {/* En-tête des jours */}
        <div className="grid" style={{ gridTemplateColumns: cols, position: "sticky", top: 0, zIndex: 8, background: "#fff" }}>
          <div style={{ borderBottom: "1px solid var(--divider)" }} />
          {days.map((d, i) => (
            <div key={i} className="py-2.5 flex items-center justify-center gap-2" style={{ borderBottom: "1px solid var(--divider)", borderLeft: "1px solid var(--divider)", background: isToday(d) ? "rgba(250,204,21,0.06)" : "" }}>
              <span className="text-[12.5px]" style={{ color: "var(--muted)" }}>{DAYS_ABBR[i]}</span>
              {isToday(d)
                ? <span className="mono text-[13px] font-bold inline-flex items-center justify-center" style={{ width: 26, height: 26, borderRadius: 8, background: "var(--accent)", color: "var(--ink)" }}>{d.getDate()}</span>
                : <span className="mono text-[13px] font-bold" style={{ color: "var(--ink)" }}>{d.getDate()}</span>}
            </div>
          ))}
        </div>
        {/* Corps */}
        <div className="grid" style={{ gridTemplateColumns: cols }}>
          <div style={{ position: "relative" }}>
            {Array.from({ length: END - START }, (_, i) => (
              <div key={i} className="text-[11px] text-right pr-2" style={{ height: PX, color: "var(--muted-2)", transform: "translateY(-6px)" }}>{START + i}h</div>
            ))}
          </div>
          {days.map((d, di) => {
            const dow = (d.getDay() + 6) % 7;
            const oh = ohFor(dow);
            const closed = !oh || !oh.ouvert;
            const debut = oh ? toMin(oh.debut) : 9 * 60;
            const fin = oh ? toMin(oh.fin) : 18 * 60;
            const dayRdvs = rdvs.filter((r) => sameDay(new Date(r.date), d));
            const absences = dayRdvs.filter((r) => r.type_rdv === "absence");
            const soinRdvs = dayRdvs.filter((r) => r.type_rdv !== "absence" && r.statut !== "Annulé");
            const isTarget = drag && drag.ghost.dayIdx === di;         // colonne visée par le geste
            const isOrigin = drag && drag.dayIdx === di;               // colonne d'origine (silhouette)
            const g = drag?.ghost;
            return (
              <div key={di} data-testid={`week-col-${di}`} ref={(el) => (colRefs.current[di] = el)} onClick={(e) => { if (closed) return; const y = e.nativeEvent.offsetY; const mins = Math.round((START * 60 + (y / PX) * 60) / 30) * 30; const dt = new Date(d); dt.setHours(0, mins, 0, 0); onSlot && onSlot(dt.toISOString()); }}
                style={{ position: "relative", height: dayHeight, borderLeft: "1px solid var(--divider)", background: isToday(d) ? "rgba(250,204,21,0.05)" : "", cursor: closed ? "default" : "pointer" }}>
                {/* Teinte de la colonne visée pendant le geste */}
                {isTarget && <div style={{ position: "absolute", inset: 0, background: (drag.blocked || drag.closed) ? DC.conflictColumn : DC.dragColumn, pointerEvents: "none", zIndex: 1 }} />}
                {Array.from({ length: END - START }, (_, i) => (<div key={i} style={{ position: "absolute", top: i * PX, left: 0, right: 0, height: 1, background: "var(--divider)", pointerEvents: "none" }} />))}
                {pas < 60 && Array.from({ length: END - START }).flatMap((_, i) => { const subs = []; for (let m = pas; m < 60; m += pas) subs.push(<div key={`s-${i}-${m}`} style={{ position: "absolute", top: (i + m / 60) * PX, left: 0, right: 0, borderTop: "1px dotted var(--divider)", pointerEvents: "none" }} />); return subs; })}
                {closed ? (
                  <div style={{ position: "absolute", inset: 0, background: HATCH, pointerEvents: "none" }}><span className="text-[11px] px-2 pt-1 inline-block" style={{ color: "var(--muted-2)" }}>Fermé</span></div>
                ) : (<>
                  {debut > START * 60 && <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: (debut / 60 - START) * PX, background: HATCH, pointerEvents: "none" }} />}
                  {fin < END * 60 && <div style={{ position: "absolute", top: (fin / 60 - START) * PX, left: 0, right: 0, bottom: 0, background: HATCH, pointerEvents: "none" }} />}
                  {oh.pause && (() => { const pd = toMin(oh.pause_debut || "12:00"), pf = toMin(oh.pause_fin || "14:00"); const PAUSE_HATCH = "repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(250,204,21,0.22) 8px, rgba(250,204,21,0.22) 17px)"; return <div style={{ position: "absolute", top: (pd / 60 - START) * PX, left: 0, right: 0, height: ((pf - pd) / 60) * PX, background: PAUSE_HATCH, pointerEvents: "none" }}><span className="text-[11px] px-2 pt-1 inline-block" style={{ color: "var(--muted)" }}>Pause</span></div>; })()}
                </>)}
                {absences.map((r) => { const st = new Date(r.date); const en = r.end ? new Date(r.end) : new Date(st.getTime() + (r.duree_min || 60) * 60000); const top = topFor(st); const h = Math.max((en - st) / 3600000 * PX, 22); return (
                  <div key={r.id} style={{ position: "absolute", top, left: 4, right: 4, height: h, background: HATCH, border: "1px solid var(--border)", borderRadius: 8, pointerEvents: "none" }}>
                    <span className="text-[11px] px-2 pt-1 inline-block" style={{ color: "var(--muted-2)" }}>{r.titre || "Créneau fermé"}</span>
                  </div>); })}
                {soinRdvs.map((r) => { const st = new Date(r.date); const dur = r.duree_min || 60; const top = topFor(st); const h = Math.max(dur / 60 * PX - 4, 34); const tone = toneForTypeId(r.type_soin, careTypes); const dotCol = STATUT_DOT(r.statut); const dragging = drag?.id === r.id; const locked = isLocked(r); const pulsing = pulseId === r.id;
                  // La carte saisie est remplacée par une silhouette à son emplacement d'origine.
                  if (dragging) return (
                    <div key={r.id} data-testid={`rdv-silhouette-${r.id}`} style={{ position: "absolute", top: top + 2, left: 4, right: 4, height: h, background: DC.silBg, border: `1.5px dashed ${DC.silBorder}`, borderRadius: 7, padding: "4px 8px", pointerEvents: "none", zIndex: 2 }}>
                      <span style={{ fontSize: 10.5, color: DC.silText }}>Avant · {hm(st.getHours() * 60 + st.getMinutes())}</span>
                    </div>);
                  return (
                  <div key={r.id} data-testid={`rdv-card-${r.id}`}
                    onPointerDown={(e) => { if (e.button !== undefined && e.button !== 0) return; beginPending(e, r, di, "move"); }}
                    onClick={(e) => { e.stopPropagation(); if (suppressClickRef.current) { suppressClickRef.current = false; return; } onOpenRdv ? onOpenRdv(r.id) : (r.soin_id && nav(`/soins/${r.soin_id}`)); }}
                    style={{ position: "absolute", top: top + 2, left: 4, right: 4, minHeight: h, background: pulsing ? DC.pulseBg : tone.bg, border: `${(selectedId === r.id || pulsing) ? 2 : 1}px solid ${(selectedId === r.id || pulsing) ? DC.dragBorder : tone.border}`, borderRadius: 7, padding: "5px 8px", cursor: locked ? "default" : "grab", overflow: "hidden", opacity: drag ? 0.45 : 1, boxShadow: pulsing ? `0 0 0 5px ${DC.pulseHalo}` : "none", transition: "background .6s ease, box-shadow .6s ease", zIndex: 3, touchAction: "pan-y" }}>
                    <div className="text-[11.5px] font-semibold leading-tight" style={{ color: tone.ink }}>{r.patient_nom || r.titre || "RDV"}</div>
                    <div className="flex items-center gap-1 mt-0.5"><span style={{ width: 6, height: 6, borderRadius: 99, background: dotCol, display: "inline-block" }} /><span className="mono text-[11px]" style={{ color: tone.time }}>{`${String(st.getHours()).padStart(2, "0")}:${String(st.getMinutes()).padStart(2, "0")}`}</span><span className="text-[10.5px]" style={{ color: tone.time }}>· {fmtDuree(dur)}</span></div>
                    {!locked && (
                      <div data-testid={`rdv-resize-${r.id}`} onPointerDown={(e) => { if (e.button !== undefined && e.button !== 0) return; beginPending(e, r, di, "resize"); }} style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 9, cursor: "ns-resize", display: "flex", alignItems: "center", justifyContent: "center", touchAction: "none" }}>
                        <span style={{ width: 20, height: 3, borderRadius: 2, background: DC.handleRest }} />
                      </div>
                    )}
                  </div>); })}
                {/* Repères début/fin + carte saisie + bulle horaire sur la colonne visée */}
                {isTarget && (() => {
                  const topPx = (g.startMin / 60 - START) * PX, endPx = ((g.startMin + g.dur) / 60 - START) * PX;
                  const warn = drag.blocked || drag.closed;
                  const col = warn ? DC.conflict : DC.dragBorder;
                  const day = days[di]; const en = g.startMin + g.dur;
                  const tip = drag.blocked
                    ? `Occupé · ${drag.name}`
                    : (drag.mode === "resize" ? `${hm(g.startMin)} – ${hm(en)} · ${g.dur} min` : `${DAYS_ABBR[di]} ${day.getDate()} · ${hm(g.startMin)} – ${hm(en)}`);
                  return (
                    <>
                      <div style={{ position: "absolute", top: topPx, left: 0, right: 0, borderTop: `1.5px dashed ${col}`, pointerEvents: "none", zIndex: 7 }} />
                      <div style={{ position: "absolute", top: endPx, left: 0, right: 0, borderTop: `1.5px dashed ${col}`, pointerEvents: "none", zIndex: 7 }} />
                      <div data-testid="rdv-drag-card" style={{ position: "absolute", top: topPx + 2, left: 4, right: 4, height: Math.max(g.dur / 60 * PX - 4, 24), background: warn ? DC.conflictBg : DC.dragBg, border: `2px solid ${col}`, borderRadius: 10, padding: "5px 8px", pointerEvents: "none", zIndex: 50, transform: "scale(1.035)", boxShadow: "0 14px 30px rgba(47,93,74,.28)", overflow: "visible" }}>
                        <div className="text-[12px] font-semibold leading-tight" style={{ color: warn ? DC.conflict : DC.dragInk }}>{drag.r.patient_nom || drag.r.titre || "RDV"}</div>
                        <div className="flex items-center gap-1 mt-0.5"><span className="mono text-[11px]" style={{ color: DC.dragTime }}>{hm(g.startMin)} → {hm(en)}</span><span className="text-[10.5px]" style={{ color: DC.dragMeta }}>· {g.dur} min</span></div>
                        <div data-testid="rdv-drag-tip" style={{ position: "absolute", top: -30, left: "50%", transform: "translateX(-50%)", background: warn ? DC.conflict : DC.dragTip, color: "#fff", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, padding: "5px 9px", borderRadius: 7, whiteSpace: "nowrap", boxShadow: "0 4px 12px rgba(0,0,0,.18)" }}>{tip}</div>
                      </div>
                    </>
                  );
                })()}
                {isToday(d) && <NowLine START={START} END={END} PX={PX} />}
              </div>
            );
          })}
        </div>
      </div>
      </div>
      </div>
      {/* Dégradé de bord dans le sens du geste */}
      {swipe.hint.show && (
        <div style={{ position: "absolute", top: 0, bottom: 0, width: 140, [swipe.hint.dir === 1 ? "right" : "left"]: 0, background: `linear-gradient(${swipe.hint.dir === 1 ? "to right" : "to left"}, transparent 0%, ${SWIPE_COLORS.glowSoft} 60%, ${SWIPE_COLORS.glowEdge} 100%)`, opacity: Math.max(0.35, swipe.hint.progress), transition: "opacity .2s ease", pointerEvents: "none", zIndex: 20 }} />
      )}
      {/* Bulle d'indication (passé/futur + semaine visée) */}
      {swipe.hint.show && (
        <div data-testid="week-swipe-hint" style={{ position: "absolute", top: "50%", [swipe.hint.dir === 1 ? "right" : "left"]: 14, transform: `translateY(-50%) scale(${0.9 + 0.1 * swipe.hint.progress})`, display: "flex", alignItems: "center", gap: 8, background: SWIPE_COLORS.hintBg, color: "#fff", borderRadius: 999, padding: "8px 14px", boxShadow: "0 10px 24px rgba(47,93,74,.28)", opacity: Math.max(0.35, swipe.hint.progress), transition: "opacity .2s ease, transform .15s ease", pointerEvents: "none", zIndex: 21 }}>
          {swipe.hint.dir === -1 && <ArrowLeft style={{ width: 14, height: 14 }} />}
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.15 }}>
            <span style={{ fontSize: 10.5, color: SWIPE_COLORS.hintSub }}>{swipe.hint.progress < 1 ? "Glisser encore…" : (swipe.hint.dir === 1 ? "Semaine suivante" : "Semaine précédente")}</span>
            <span style={{ fontSize: 12.5, fontWeight: 600 }}>{swipe.hint.range}</span>
          </div>
          {swipe.hint.dir === 1 && <ArrowRight style={{ width: 14, height: 14 }} />}
        </div>
      )}
      {/* Pastille de chargement (si la semaine met plus de 300 ms à charger) */}
      {swipe.showLoader && (
        <div data-testid="week-loading" className="sx-fade-in" style={{ position: "absolute", top: 140, left: "50%", transform: "translateX(-50%)", zIndex: 30, display: "flex", alignItems: "center", gap: 10, background: "#fff", border: `1px solid ${SWIPE_COLORS.loaderBorder}`, borderRadius: 999, padding: "9px 16px", boxShadow: "0 8px 20px rgba(47,93,74,.15)", pointerEvents: "none" }}>
          <span className="sx-week-loader-ring" style={{ width: 22, height: 22, borderRadius: 999, border: `2.5px solid ${SWIPE_COLORS.loaderTrack}`, borderTopColor: SWIPE_COLORS.loaderActive, display: "inline-block" }} />
          <span style={{ fontSize: 12.5, color: SWIPE_COLORS.loaderActive }}>Chargement de la semaine…</span>
        </div>
      )}
    </div>
    {/* Bandeau d'annulation (bas, centré) */}
    {undo && (
      <div data-testid="rdv-undo-banner" style={{ position: "fixed", left: "50%", bottom: 24, transform: "translateX(-50%)", zIndex: 60, display: "flex", alignItems: "center", gap: 14, background: DC.bannerBg, color: "#fff", padding: "10px 14px", borderRadius: 12, boxShadow: "0 12px 34px rgba(0,0,0,.28)" }}>
        <Move className="w-4 h-4" style={{ color: DC.dragBg }} />
        <span style={{ fontSize: 13 }}>{undo.label}</span>
        <button data-testid="rdv-undo-btn" onClick={doUndo} style={{ background: DC.bannerBtnBg, color: DC.bannerBtnInk, fontWeight: 600, height: 32, borderRadius: 8, padding: "0 14px" }}>Annuler</button>
      </div>
    )}
    </>
  );
}

function MonthGrid({ anchor, rdvsOn, careTypes = [], openDay, onOpenRdv }) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const start = sow(first);
  const cells = Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
  return (
    <div className="bg-white sx-card p-2">
      <div className="grid grid-cols-7">
        {DAYS.map((d) => <div key={d} className="text-center text-xs font-semibold text-slate-400 py-2">{d}</div>)}
        {cells.map((d, i) => {
          const evs = rdvsOn(d);
          const inMonth = d.getMonth() === anchor.getMonth();
          return (
            <div key={i} onClick={() => openDay(d)}
              className={`min-h-[92px] border border-slate-100 p-1 cursor-pointer hover:bg-slate-50 ${inMonth ? "" : "bg-slate-50/50"} ${isToday(d) ? "ring-1 ring-yellow-300 bg-yellow-50" : ""}`}>
              <div className={`text-xs font-semibold ${inMonth ? "text-slate-700" : "text-slate-300"}`}>{d.getDate()}</div>
              <div className="space-y-0.5 mt-1">
                {evs.slice(0, 3).map((r) => { const tn = r.type_rdv === "absence" ? null : toneForTypeId(r.type_soin, careTypes); return (
                  <div key={r.id} onClick={(e) => { e.stopPropagation(); onOpenRdv ? onOpenRdv(r.id) : null; }}
                    className="text-[10px] rounded px-1 py-0.5 truncate border"
                    style={tn ? { background: tn.bg, borderColor: tn.border, color: tn.ink } : { background: "#f1f0ec", borderColor: "#e3e3de", color: "#6b7180" }}>
                    {r.patient_nom || r.titre || "RDV"}
                  </div>); })}
                {evs.length > 3 && <div className="text-[10px] text-slate-400">+{evs.length - 3}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QuarterGrid({ anchor, rdvsOn, careTypes = [], openDay }) {
  const months = [0, 1, 2].map((k) => new Date(anchor.getFullYear(), anchor.getMonth() + k, 1));
  return (
    <div className="grid md:grid-cols-3 gap-4">
      {months.map((m, mi) => {
        const start = sow(new Date(m.getFullYear(), m.getMonth(), 1));
        const cells = Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
        return (
          <div key={mi} className="bg-white sx-card p-3">
            <div className="font-display font-semibold text-slate-800 text-sm mb-2 capitalize">{MONTHS[m.getMonth()]} {m.getFullYear()}</div>
            <div className="grid grid-cols-7 gap-0.5">
              {DAYS_SHORT.map((d, i) => <div key={i} className="text-center text-[10px] text-slate-400">{d}</div>)}
              {cells.map((d, i) => {
                const dayEvs = rdvsOn(d); const has = dayEvs.length > 0;
                const dts = new Set(dayEvs.filter((e) => e.type_rdv !== "absence").map((e) => e.type_soin));
                const dtone = dts.size === 1 ? toneForTypeId([...dts][0], careTypes) : null;
                const inMonth = d.getMonth() === m.getMonth();
                return (
                  <button key={i} onClick={() => openDay(d)}
                    className={`aspect-square rounded-md text-[11px] flex items-center justify-center relative ${inMonth ? "text-slate-700 hover:bg-slate-100" : "text-slate-300"} ${isToday(d) ? "bg-yellow-100 font-bold" : ""}`}>
                    {d.getDate()}
                    {has && inMonth && <span className="absolute bottom-0.5 w-1 h-1 rounded-full" style={{ background: dtone ? dtone.time : "#16a34a" }} />}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SlotDialog({ open, onClose, onSaved }) {
  const confirm = useConfirm();
  const blank = () => ({ start: "", end: "", objet: "NON DISPONIBLE", repetition: false });
  const [ranges, setRanges] = useState([blank()]);
  const [existing, setExisting] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editF, setEditF] = useState(blank());
  const [deletingId, setDeletingId] = useState(null);
  const endRefs = useRef([]);

  const loadExisting = () => api.get("/slots").then((r) => setExisting(r.data)).catch(() => {});
  useEffect(() => { if (open) { setRanges([blank()]); setEditId(null); loadExisting(); } }, [open]);

  const setRange = (i, patch) => setRanges((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const onStartChange = (i, val) => {
    setRanges((rs) => rs.map((r, idx) => (idx === i ? { ...r, start: val, end: (!r.end || r.end <= val) ? val : r.end } : r)));
    setTimeout(() => { const el = endRefs.current[i]; if (el) { el.focus(); try { el.showPicker && el.showPicker(); } catch (_) { /* noop */ } } }, 60);
  };
  const addRange = () => setRanges((rs) => [...rs, blank()]);
  const removeRange = (i) => setRanges((rs) => (rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs));
  const saveAll = async () => {
    const valid = ranges.filter((r) => r.start && r.end);
    if (!valid.length) { toast.error("Renseignez au moins une plage"); return; }
    try {
      for (const r of valid) await api.post("/slots/close", { objet: r.objet || "NON DISPONIBLE", repetition: !!r.repetition, start: new Date(r.start).toISOString(), end: new Date(r.end).toISOString() });
      toast.success(valid.length > 1 ? `${valid.length} créneaux fermés` : "Créneau fermé");
      onSaved(); setRanges([blank()]); loadExisting();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (deletingId || !(await confirm({ message: "Supprimer ce créneau fermé ?" }))) return; setDeletingId(id); try { await api.delete(`/slots/${id}`); loadExisting(); onSaved(); toast.success("Supprimé"); } finally { setDeletingId(null); } };
  const startEdit = (s) => { setEditId(s.id); setEditF({ start: toLocalDT(s.date), end: toLocalDT(s.end), objet: s.titre || "NON DISPONIBLE", repetition: !!s.repetition }); };
  const saveEdit = async () => {
    if (!editF.start || !editF.end) { toast.error("Renseignez la plage"); return; }
    await api.put(`/slots/${editId}`, { objet: editF.objet, repetition: !!editF.repetition, start: new Date(editF.start).toISOString(), end: new Date(editF.end).toISOString() });
    setEditId(null); loadExisting(); onSaved(); toast.success("Modifié");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Fermer un créneau</DialogTitle><DialogDescription>Bloquez une ou plusieurs plages horaires d'indisponibilité dans l'agenda.</DialogDescription></DialogHeader>

        <div className="space-y-3">
          {ranges.map((r, i) => (
            <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-3" data-testid={`slot-range-${i}`}>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-700">Début</label>
                  <input data-testid={`slot-start-${i}`} type="datetime-local" value={r.start} onChange={(e) => onStartChange(i, e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300" /></div>
                <div><label className="text-sm font-medium text-slate-700">Fin</label>
                  <input data-testid={`slot-end-${i}`} ref={(el) => (endRefs.current[i] = el)} type="datetime-local" value={r.end} onChange={(e) => setRange(i, { end: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300" /></div>
              </div>
              <div className="flex items-end gap-3">
                <div className="flex-1"><label className="text-sm font-medium text-slate-700">Objet</label>
                  <input data-testid={`slot-objet-${i}`} value={r.objet} onChange={(e) => setRange(i, { objet: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300" /></div>
                <label className="flex items-center gap-2 text-sm text-slate-700 pb-2.5"><input type="checkbox" checked={r.repetition} onChange={(e) => setRange(i, { repetition: e.target.checked })} />Hebdo.</label>
                {ranges.length > 1 && <button data-testid={`slot-remove-${i}`} onClick={() => removeRange(i)} className="pb-2.5 text-red-500" aria-label="Retirer ce créneau"><Trash2 className="w-4 h-4" /></button>}
              </div>
            </div>
          ))}
          <button data-testid="slot-add-range" onClick={addRange} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-slate-900"><Plus className="w-4 h-4" />Ajouter un autre créneau</button>
        </div>

        {existing.length > 0 && (
          <div className="mt-4 border-t border-slate-100 pt-4">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Créneaux fermés existants</div>
            <div className="space-y-1.5 max-h-52 overflow-y-auto">
              {existing.map((s) => editId === s.id ? (
                <div key={s.id} className="rounded-lg border border-slate-200 p-2 space-y-2" data-testid={`slot-existing-${s.id}`}>
                  <div className="grid grid-cols-2 gap-2">
                    <input type="datetime-local" data-testid={`slot-edit-start-${s.id}`} value={editF.start} onChange={(e) => setEditF({ ...editF, start: e.target.value })} className="px-2 py-1.5 rounded border border-slate-300 text-sm" />
                    <input type="datetime-local" data-testid={`slot-edit-end-${s.id}`} value={editF.end} onChange={(e) => setEditF({ ...editF, end: e.target.value })} className="px-2 py-1.5 rounded border border-slate-300 text-sm" />
                  </div>
                  <div className="flex items-center gap-2">
                    <input value={editF.objet} onChange={(e) => setEditF({ ...editF, objet: e.target.value })} className="flex-1 px-2 py-1.5 rounded border border-slate-300 text-sm" />
                    <button data-testid={`slot-edit-save-${s.id}`} onClick={saveEdit} className="text-emerald-600"><Check className="w-4 h-4" /></button>
                    <button onClick={() => setEditId(null)} className="text-slate-400"><X className="w-4 h-4" /></button>
                  </div>
                </div>
              ) : (
                <div key={s.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm" data-testid={`slot-existing-${s.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800 truncate">{s.titre || "NON DISPONIBLE"}{s.repetition ? " · hebdo" : ""}</div>
                    <div className="mono text-[11.5px] text-slate-500">{fmtRange(s.date, s.end)}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`slot-edit-${s.id}`} onClick={() => startEdit(s)} className="text-slate-400 hover:text-slate-700"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`slot-del-${s.id}`} disabled={deletingId === s.id} onClick={() => del(s.id)} className="text-red-500 disabled:opacity-40"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Annuler</button>
          <button data-testid="slot-save" onClick={saveAll} className="px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-medium">Fermer le(s) créneau(x)</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OpeningHours({ hours, onSaved }) {
  const [rows, setRows] = useState([]);
  useEffect(() => setRows(DAYS.map((_, i) => hours.find((x) => x.jour === i) || { jour: i, ouvert: i < 5, debut: "09:00", fin: "18:00", pause: false, pause_debut: "12:00", pause_fin: "14:00" })), [hours]);
  const upd = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const save = async () => { await api.put("/opening-hours", rows); toast.success("Créneaux enregistrés"); onSaved(rows); };
  const GRID = "116px 128px 128px 78px 124px 124px";
  return (
    <div className="sx-card p-5">
      <h3 className="font-display font-semibold text-slate-900 mb-1">Créneaux d'ouverture par défaut</h3>
      <p className="text-[12.5px] text-slate-500 mb-4">Une ligne par jour : horaires d'ouverture et pause déjeuner éventuelle.</p>
      <div className="overflow-x-auto">
        <div style={{ minWidth: 700 }}>
          <div className="grid items-center px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wider" style={{ gridTemplateColumns: GRID, gap: 12, color: "var(--muted-2)", letterSpacing: ".05em" }}>
            <span>Jour</span><span>Ouverture</span><span>Fermeture</span><span>Pause</span><span>Début pause</span><span>Fin pause</span>
          </div>
          {rows.map((r, i) => (
            <div key={i} className="grid items-center px-1 py-1.5" style={{ gridTemplateColumns: GRID, gap: 12, opacity: r.ouvert ? 1 : 0.55, borderTop: "1px solid var(--divider)" }}>
              <label className="flex items-center gap-2 text-[13px] font-medium text-slate-700">
                <input type="checkbox" data-testid={`oh-open-${i}`} checked={r.ouvert} onChange={(e) => upd(i, { ouvert: e.target.checked })} />{DAYS[i]}
              </label>
              <input type="time" value={r.debut} disabled={!r.ouvert} onChange={(e) => upd(i, { debut: e.target.value })} className="px-2 py-1.5 rounded-lg border border-slate-300 text-sm disabled:opacity-40" />
              <input type="time" value={r.fin} disabled={!r.ouvert} onChange={(e) => upd(i, { fin: e.target.value })} className="px-2 py-1.5 rounded-lg border border-slate-300 text-sm disabled:opacity-40" />
              <label className="flex items-center gap-1.5 text-[13px] text-slate-600">
                <input type="checkbox" data-testid={`oh-pause-${i}`} checked={!!r.pause} disabled={!r.ouvert} onChange={(e) => upd(i, { pause: e.target.checked })} />Pause
              </label>
              <input type="time" data-testid={`oh-pause-debut-${i}`} value={r.pause_debut || "12:00"} disabled={!r.ouvert || !r.pause} onChange={(e) => upd(i, { pause_debut: e.target.value })} className="px-2 py-1.5 rounded-lg border border-slate-300 text-sm disabled:opacity-40" />
              <input type="time" data-testid={`oh-pause-fin-${i}`} value={r.pause_fin || "14:00"} disabled={!r.ouvert || !r.pause} onChange={(e) => upd(i, { pause_fin: e.target.value })} className="px-2 py-1.5 rounded-lg border border-slate-300 text-sm disabled:opacity-40" />
            </div>
          ))}
        </div>
      </div>
      <button data-testid="oh-save" onClick={save} className="mt-4 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium">Enregistrer</button>
    </div>
  );
}

function FermeturesPonctuelles({ onChanged }) {
  const confirm = useConfirm();
  const blank = () => ({ start: "", end: "", objet: "NON DISPONIBLE", repetition: false });
  const [f, setF] = useState(blank());
  const [existing, setExisting] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editF, setEditF] = useState(blank());
  const endRef = useRef(null);
  const load = () => api.get("/slots").then((r) => setExisting(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const onStart = (val) => {
    setF((c) => ({ ...c, start: val, end: (!c.end || c.end <= val) ? val : c.end }));
    setTimeout(() => { const el = endRef.current; if (el) { el.focus(); try { el.showPicker && el.showPicker(); } catch (_) { /* noop */ } } }, 60);
  };
  const add = async () => {
    if (!f.start || !f.end) { toast.error("Renseignez la plage"); return; }
    try { await api.post("/slots/close", { objet: f.objet || "NON DISPONIBLE", repetition: !!f.repetition, start: new Date(f.start).toISOString(), end: new Date(f.end).toISOString() }); toast.success("Créneau fermé"); setF(blank()); load(); onChanged && onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!(await confirm({ message: "Supprimer ce créneau fermé ?" }))) return; await api.delete(`/slots/${id}`); load(); onChanged && onChanged(); toast.success("Supprimé"); };
  const startEdit = (s) => { setEditId(s.id); setEditF({ start: toLocalDT(s.date), end: toLocalDT(s.end), objet: s.titre || "NON DISPONIBLE", repetition: !!s.repetition }); };
  const saveEdit = async () => {
    if (!editF.start || !editF.end) { toast.error("Renseignez la plage"); return; }
    await api.put(`/slots/${editId}`, { objet: editF.objet, repetition: !!editF.repetition, start: new Date(editF.start).toISOString(), end: new Date(editF.end).toISOString() });
    setEditId(null); load(); onChanged && onChanged(); toast.success("Modifié");
  };
  return (
    <div className="sx-card p-5" data-testid="fermetures-ponctuelles">
      <h3 className="font-display font-semibold text-slate-900 mb-1">Fermetures ponctuelles</h3>
      <p className="text-[12.5px] text-slate-500 mb-4">Bloquez une plage précise (congés, absence…). Cochez « Hebdo. » pour une fermeture récurrente.</p>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div><label className="text-[12.5px] font-medium text-slate-700 block">Début</label><input data-testid="fp-start" type="datetime-local" value={f.start} onChange={(e) => onStart(e.target.value)} className="mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm" /></div>
        <div><label className="text-[12.5px] font-medium text-slate-700 block">Fin</label><input data-testid="fp-end" ref={endRef} type="datetime-local" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm" /></div>
        <div className="flex-1 min-w-[160px]"><label className="text-[12.5px] font-medium text-slate-700 block">Objet</label><input data-testid="fp-objet" value={f.objet} onChange={(e) => setF({ ...f, objet: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm" /></div>
        <label className="flex items-center gap-2 text-sm text-slate-700 pb-2.5"><input type="checkbox" checked={f.repetition} onChange={(e) => setF({ ...f, repetition: e.target.checked })} />Hebdo.</label>
        <button data-testid="fp-add" onClick={add} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium"><Plus className="w-4 h-4" />Fermer</button>
      </div>
      {existing.length === 0 ? (
        <div className="text-[13px] text-slate-400">Aucune fermeture ponctuelle.</div>
      ) : (
        <div className="space-y-1.5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Fermetures enregistrées</div>
          {existing.map((s) => editId === s.id ? (
            <div key={s.id} className="rounded-lg border border-slate-200 p-2" data-testid={`fp-existing-${s.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <input type="datetime-local" data-testid={`fp-edit-start-${s.id}`} value={editF.start} onChange={(e) => setEditF({ ...editF, start: e.target.value })} className="px-2 py-1.5 rounded border border-slate-300 text-sm" />
                <input type="datetime-local" data-testid={`fp-edit-end-${s.id}`} value={editF.end} onChange={(e) => setEditF({ ...editF, end: e.target.value })} className="px-2 py-1.5 rounded border border-slate-300 text-sm" />
                <input value={editF.objet} onChange={(e) => setEditF({ ...editF, objet: e.target.value })} className="flex-1 min-w-[120px] px-2 py-1.5 rounded border border-slate-300 text-sm" />
                <button data-testid={`fp-edit-save-${s.id}`} onClick={saveEdit} className="text-emerald-600"><Check className="w-4 h-4" /></button>
                <button onClick={() => setEditId(null)} className="text-slate-400"><X className="w-4 h-4" /></button>
              </div>
            </div>
          ) : (
            <div key={s.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm" data-testid={`fp-existing-${s.id}`}>
              <div className="min-w-0"><div className="font-medium text-slate-800 truncate">{s.titre || "NON DISPONIBLE"}{s.repetition ? " · hebdo" : ""}</div><div className="mono text-[11.5px] text-slate-500">{fmtRange(s.date, s.end)}</div></div>
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid={`fp-edit-${s.id}`} onClick={() => startEdit(s)} className="text-slate-400 hover:text-slate-700"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`fp-del-${s.id}`} onClick={() => del(s.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
